import React, { useMemo } from "react";
import { filter, get, includes } from "lodash";
import { canCreateChild, canRename } from "src/util/customerConfig";
import {
  useGroupTypes,
  useWorkspaceFeatureFlags,
  useAllocationConfig,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";
import {
  useAuth,
  useAllocationProject,
} from "src/contexts/global/GlobalContext";
import { hasAdminRole } from "src/services/auth";
import AddVacantRoleModal from "src/components/AddVacantRoleModal";
import AddVacantRoleForGroupModal from "src/components/AddVacantRoleForGroupModal";
import MoveGroupEnhancedModal from "src/components/MoveGroupEnhancedModal";
import CloneHierarchyModal from "src/components/CloneHierarchyModal";
import MoveGroupModal from "src/components/MoveGroupModal";
import { GLOBAL_ACTION_KEYS } from "src/components/ConfiguredActionMenu";
import { useUpdateVacanciesInOrgChart } from "src/hooks/useUpdateVacanciesInOrgChart";
import useUpdateTeamDetails from "src/hooks/useUpdateTeamDetails";
import ImagePicker from "src/components/ImagePicker";
import HideTeamModal from "src/components/HideTeamModal";
import {
  isRealTimeAllocationsEnabled,
  isAddingMemberEnabled,
} from "src/util/allocations";
import { isGroupActive } from "src/util/group";
import { getActionMenuLabels } from "src/util/actionMenuLabels";
import TeamRenameModal from "src/components/Team/TeamRenameModal";
import AddNewTeamModal from "../Modals/AddNewTeamModal";
import AddMemberToTeamModal from "../Modals/AddMemberToTeamModal";
import TeamDescriptionEditModal from "../Modals/TeamDescriptionEditModal";
import useHideTeam from "./useHideTeam";

const TEAM_ACTION_MENU_TYPE = {
  CHANGE_TEAM_DETAILS: "change_team_details",
  RENAME_TEAM: "rename_team",
  ADD_TEAM: "add_team",
  ADD_MEMBER: "add_member",
  DOWNLOAD: "download",
  MOVE_TEAM: "move_team",
  ADVANCED_MOVE: "advanced_move",
  REMOVE_TEAM: "remove_team",
  CLONE_HIERARCHY: "clone_hierarchy",
  SELECT_COVER_IMAGE: "select_cover_image",
  ADD_VACANT_ROLE: "add_vacant_role",
  ADD_VACANT_ROLE_FOR_GROUP: "add_vacant_role_for_group",
};

// Logical order: Group 1 (Structure) → Group 2 (Details) → Group 3 (Members) → Group 4 (Export)
const defaultActionMenuTypes = [
  TEAM_ACTION_MENU_TYPE.ADD_TEAM,
  TEAM_ACTION_MENU_TYPE.MOVE_TEAM,
  TEAM_ACTION_MENU_TYPE.ADVANCED_MOVE,
  TEAM_ACTION_MENU_TYPE.CLONE_HIERARCHY,
  TEAM_ACTION_MENU_TYPE.REMOVE_TEAM,
  TEAM_ACTION_MENU_TYPE.CHANGE_TEAM_DETAILS,
  TEAM_ACTION_MENU_TYPE.RENAME_TEAM,
  TEAM_ACTION_MENU_TYPE.SELECT_COVER_IMAGE,
  TEAM_ACTION_MENU_TYPE.ADD_MEMBER,
  TEAM_ACTION_MENU_TYPE.ADD_VACANT_ROLE,
  TEAM_ACTION_MENU_TYPE.ADD_VACANT_ROLE_FOR_GROUP,
  TEAM_ACTION_MENU_TYPE.DOWNLOAD,
];

const useTeamActions = ({
  team,
  actionMenuTypes = defaultActionMenuTypes,
  showChangesEnabled,
  onChangeDetailsClick,
  onRenameTeamClick,
  runGroupCacheUpdateOnAddition,
}) => {
  const canHide = useHideTeam(team);
  const groupTypes = useGroupTypes();
  const auth = useAuth();
  const workspace = useWorkspace();
  const allocationProject = useAllocationProject();
  const {
    enableEnhancedMove: moveGroupEnhancedEnabledFlag,
    enableCloneHierarchy: enableCloneHierarchyFlag,
    enableAddVacantRolesToTeamDetailsOrgChart: showAddVacantRolesFlag,
  } = useWorkspaceFeatureFlags();
  const {
    enableAddVacantRole,
    enableMoveGroups: moveGroupEnabled,
    vacantRoleLabel,
    demandLabel,
    supplyLabel,
    addTeamBtnLabel,
  } = useAllocationConfig();

  const allocationsEnabled =
    isRealTimeAllocationsEnabled({
      user: auth?.user ?? auth,
      workspace,
      allocationProject,
    }) && isAddingMemberEnabled(workspace, team);
  const canAddMember =
    allocationsEnabled &&
    isGroupActive(team) &&
    !get(groupTypes, [team?.type, "disableToHaveDirectReports"], false);
  const { handleUpdateTeamDetails, canUpdateGroupAttributes } =
    useUpdateTeamDetails({ team });
  const { onVacanciesAdded } = useUpdateVacanciesInOrgChart({
    team,
    groupTypes,
  });

  const actions = useMemo(() => {
    const config = get(groupTypes, team.type, {});

    const moveGroupEnhancedEnabled =
      hasAdminRole(auth) && moveGroupEnhancedEnabledFlag;

    const cloneHierarchyEnabled =
      hasAdminRole(auth) && enableCloneHierarchyFlag;

    const { addLabel, moveLabel, removeLabel } = getActionMenuLabels(
      team,
      groupTypes,
      { supplyLabel, demandLabel, addTeamBtnLabel }
    );

    const user = auth?.user ?? auth;
    const canRenameTeam =
      canRename(user, groupTypes, team) && isGroupActive(team);

    // Order: Group 1 (Structure) → Group 2 (Details) → Group 3 (Members) → Group 4 (Export)
    const defaultActions = [
      // Group 1: Team/Group structure
      {
        type: TEAM_ACTION_MENU_TYPE.ADD_TEAM,
        isHidden: !canCreateChild(groupTypes, team) || showChangesEnabled,
        label: addLabel,
        modal: {
          Component: AddNewTeamModal,
          name: "AddNewTeamModal",
          props: { group: team, runGroupCacheUpdateOnAddition },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.MOVE_TEAM,
        isHidden: !moveGroupEnabled || showChangesEnabled,
        label: moveLabel,
        modal: {
          Component: MoveGroupModal,
          name: "MoveGroupModal",
          props: { group: team, groupTypes },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.ADVANCED_MOVE,
        isHidden: !moveGroupEnhancedEnabled || showChangesEnabled,
        label: "Advanced move",
        modal: {
          Component: MoveGroupEnhancedModal,
          name: "MoveGroupEnhancedModal",
          props: { rootGroup: team, groupTypes },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.CLONE_HIERARCHY,
        isHidden: !cloneHierarchyEnabled || showChangesEnabled,
        label: "Clone hierarchy",
        modal: {
          Component: CloneHierarchyModal,
          name: "CloneHierarchyModal",
          props: { cloneToTeam: team, groupTypes },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.REMOVE_TEAM,
        isHidden: !canHide || showChangesEnabled,
        label: removeLabel,
        testId: "remove-team-action",
        modal: {
          Component: HideTeamModal,
          name: "HideTeamModal",
          props: { group: team },
        },
      },
      // Group 2: Team details
      {
        type: TEAM_ACTION_MENU_TYPE.CHANGE_TEAM_DETAILS,
        isHidden: !team.type || showChangesEnabled,
        label: `Change details`,
        onClick: onChangeDetailsClick,
        modal: onChangeDetailsClick
          ? undefined
          : {
              Component: TeamDescriptionEditModal,
              name: "TeamDescriptionEditModal",
              props: { group: team },
            },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.RENAME_TEAM,
        isHidden: !canRenameTeam || showChangesEnabled,
        label: "Rename team",
        onClick: onRenameTeamClick,
        modal: onRenameTeamClick
          ? undefined
          : {
              Component: TeamRenameModal,
              name: "TeamRenameModal",
              props: { team },
            },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.SELECT_COVER_IMAGE,
        isHidden: !canUpdateGroupAttributes || showChangesEnabled,
        label: "Select cover image",
        modal: {
          // eslint-disable-next-line react/prop-types
          Component: ({ onHideModal, isVisible }) => (
            <ImagePicker
              attributes={get(team, "attributes")}
              onUpdate={handleUpdateTeamDetails}
              isVisible={isVisible}
              onHideModal={onHideModal}
            />
          ),
          name: "UpdateBackgroundModal",
        },
      },
      // Group 3: Member management
      {
        type: TEAM_ACTION_MENU_TYPE.ADD_MEMBER,
        isHidden: !canAddMember || showChangesEnabled,
        label: "Add member",
        modal: {
          Component: AddMemberToTeamModal,
          name: "AddMemberToTeamModal",
          props: { group: team },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.ADD_VACANT_ROLE,
        isHidden:
          !config.isDemand || !enableAddVacantRole || !showAddVacantRolesFlag,
        label: `Add ${vacantRoleLabel || "Vacant role"}`,
        modal: {
          Component: AddVacantRoleModal,
          name: "AddVacantRoleModal",
          props: {
            targetGroupId: team.id,
            groupTypes,
            onVacanciesAdded,
          },
        },
      },
      {
        type: TEAM_ACTION_MENU_TYPE.ADD_VACANT_ROLE_FOR_GROUP,
        isHidden:
          !config.isSupply || !enableAddVacantRole || !showAddVacantRolesFlag,
        label: `Add ${vacantRoleLabel || "Vacant role"}`,
        modal: {
          Component: AddVacantRoleForGroupModal,
          name: "AddVacantRoleForGroupModal",
          props: {
            group: team,
            onVacanciesAdded,
          },
        },
      },
      // Group 4: Export
      {
        type: TEAM_ACTION_MENU_TYPE.DOWNLOAD,
        globalAction: GLOBAL_ACTION_KEYS.DOWNLOAD,
      },
    ];

    const filteredActions = filter(defaultActions, (action) => {
      return includes(actionMenuTypes, action.type);
    });

    return filteredActions;
  }, [
    groupTypes,
    team,
    auth,
    canAddMember,
    demandLabel,
    supplyLabel,
    addTeamBtnLabel,
    moveGroupEnhancedEnabledFlag,
    enableCloneHierarchyFlag,
    showChangesEnabled,
    onChangeDetailsClick,
    onRenameTeamClick,
    runGroupCacheUpdateOnAddition,
    moveGroupEnabled,
    canHide,
    canUpdateGroupAttributes,
    enableAddVacantRole,
    showAddVacantRolesFlag,
    vacantRoleLabel,
    handleUpdateTeamDetails,
    onVacanciesAdded,
    actionMenuTypes,
  ]);

  return actions;
};

export default useTeamActions;
